<template>
  <div class="f1-season-box-view" ref="seasonBox">
    <div class="f1-season-box-view__tab-nav" v-if="previousRace">
      <button class="f1-season-box-view__tab-nav-item" :class="activeTabItem('next')" @click="changeActiveTab('next')" v-if="nextRace">
        {{$t('seasonBox.nextRace')}}
      </button>
      <button class="f1-season-box-view__tab-nav-item" :class="activeTabItem('previous')" @click="changeActiveTab('previous')">
        {{$t('seasonBox.latestResults')}}
      </button>
      <button class="f1-season-box-view__tab-nav-item" :class="activeTabItem('standing')" @click="changeActiveTab('standing')">
        {{$t('seasonBox.standings')}}
      </button>
    </div>
    <div class="f1-season-box-view__content-container">
      <transition name="fade">
        <div class="f1-season-box-view__tab-content f1-season-box-view__tab-content--next-race" v-if="view === 'next' && nextRace" :key="'next'">
          <div class="f1-season-box-view__headline-col">
            <span class="f1-season-box-view__headline">
              {{nextRace.seasonEventTracks[nextRace.seasonEventTracks.length - 1].name}}
            </span>
            <span class="f1-season-box-view__subheadline">
              <icon-location class="svg-icon svg-icon-white"/>
              {{nextRace.seasonEventTracks[nextRace.seasonEventTracks.length - 1].city}}, {{nextRace.eventCountry}}
            </span>
          </div>
          <div class="f1-season-box-view__track-col">
            <div class="f1-season-box-view__track-circle">
              <track-circle class="f1-season-box-view__track-circle-timer" ref="trackCircle" viewBox="0 0 556 556"/>
              <race-track class="f1-season-box-view__track" :track-name="nextRaceTrackId"/>
              <div class="f1-season-box-view__track-date-container">
                <div class="f1-season-box-view__date-row">
                  <icon-calendar class="f1-season-box-view__date-icon svg-icon svg-icon-white"/>
                  <span class="f1-season-box-view__date">
                    {{nextRaceDate}}
                  </span>
                </div>
                <div class="f1-season-box-view__date-row">
                  <icon-time class="f1-season-box-view__date-icon svg-icon svg-icon-white"/>
                  {{nextRaceTime}}
                  <small class="f1-season-box-view__fact-small">
                    ({{ $t('raceTrack.localTime') }})
                  </small>
                </div>
                <div class="f1-season-box-view__date-row">
                  <icon-time class="f1-season-box-view__date-icon svg-icon svg-icon-white"/>
                  {{nextRaceViewerTime}}
                  <small class="f1-season-box-view__fact-small">
                    ({{ $t('raceTrack.yourTime') }})
                  </small>
                </div>
              </div>
            </div>
          </div>
          <div class="f1-season-box-view__date-container">
            <div class="f1-season-box-view__number-date-container">
              <div class="f1-season-box-view__date-row">
                <icon-calendar class="f1-season-box-view__date-icon svg-icon svg-icon-white"/>
                <span class="f1-season-box-view__date">
                  {{nextRaceDate}}
                </span>
              </div>
              <div class="f1-season-box-view__date-row">
                <icon-time class="f1-season-box-view__date-icon svg-icon svg-icon-white"/>
                {{nextRaceTime}}
                <small class="f1-season-box-view__fact-small">
                  ({{ $t('raceTrack.localTime') }})
                </small>
              </div>
              <div class="f1-season-box-view__date-row">
                <icon-time class="f1-season-box-view__date-icon svg-icon svg-icon-white"/>
                {{nextRaceViewerTime}}
                <small class="f1-season-box-view__fact-small">
                  ({{ $t('raceTrack.yourTime') }})
                </small>
              </div>
            </div>
            <countdown v-if="nextDate" :next-race-date="nextDate"/>
          </div>
        </div>
        <div class="f1-season-box-view__tab-content" v-else-if="view === 'previous'" :key="'previous'">
          <div class="f1-season-box-view__col">
            <span class="f1-season-box-view__headline">
              {{previousRace.seasonEventTracks[previousRace.seasonEventTracks.length - 1].name}}
            </span>
            <div class="f1-season-box-view__table-container">
              <table class="season-table f1-season-box-view__table">
                <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">{{$t('seasonBox.driver')}}</th>
                  <th scope="col">{{$t('seasonBox.team')}}</th>
                  <th scope="col">{{$t('seasonBox.time')}}</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="(driverResult, index) in previousRaceResults" :class="{ 'is-mercedes' : driverResult.team_ID === '817'}" :key="`prev-driver-${index}`">
                  <td>{{showIndex(driverResult) ? index + 1 : ''}}</td>
                  <td>{{driverResult.driver_firstname}} {{driverResult.driver_surname}}</td>
                  <td>{{getTeamForDriver(null, driverResult.team_ID).name}}</td>
                  <td>{{getBehind(driverResult)}}</td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div class="f1-season-box-view__tab-content f1-season-box-view__tab-content--row" v-else-if="view === 'standing'" :key="'standing'">
          <StandingsView />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import IconCalendar from 'workbench-assets/dist/icons/calendar.svg';
import IconTime from 'workbench-assets/dist/icons/time.svg';
import IconLocation from 'workbench-assets/dist/icons/location.svg';
import { RootGetterTypes } from '@/rootStore';
import StandingsView from './StandingsView.vue';
import RaceTrack from './RaceTrack.vue';
import trackCircle from '../assets/parts/amg-f1-circle.svg';
import Countdown from './Countdown.vue';
import { keywordsInBehind } from '../helpers/sort';
import {
  getBehind,
  localizedDate,
  localizedTimeFromString,
  raceToViewerTime,
} from '../helpers/helperFunctions';
import { GetterTypes } from '../store';

export default {
  components: {
    StandingsView,
    RaceTrack,
    IconCalendar,
    IconTime,
    IconLocation,
    trackCircle,
    Countdown,
  },
  data () {
    return {
      view: 'next',
      circleDone: false,
    };
  },
  methods: {
    activeTabItem (viewMode) {
      return viewMode === this.view
        ? 'f1-season-box-view__tab-nav-item--active'
        : '';
    },
    changeActiveTab (newTab) {
      if (this.view !== newTab) {
        this.view = newTab;
      }
      if (this.view === 'next') {
        this.fillCircle();
      } else {
        this.circleDone = false;
      }
    },
    getTeamForDriver (driver, teamId = null) {
      const event = this.previousRaceResults;
      let response = null;
      let scopeTeamId = teamId;

      if (event.length > 0 && !scopeTeamId) {
        event.forEach((result) => {
          if (result.driver_ID === driver.foreignId) {
            scopeTeamId = result.team_ID;
          }
        });
      }

      if (this.championship.teams.length > 0 && scopeTeamId) {
        this.championship.teams.forEach((team) => {
          if (team.foreignId === scopeTeamId) {
            response = team;
          }
        });
      }

      return response;
    },
    sortedPoints (toSort) {
      return toSort.sort((val1, val2) => val1.points > val2.points);
    },
    fillCircle () {
      if (this.circleDone || !this.$refs.trackCircle) {
        return;
      }

      this.circleDone = true;

      let pastRaceDate = new Date(`${this.seasonYear}-01-01T00:00`);
      let nextRaceDate = this.nextDate;
      const now = new Date();

      if (this.previousRace) {
        pastRaceDate = new Date(this.previousRace.raceStart);
      }
      if (nextRaceDate === 'unknown') {
        nextRaceDate = pastRaceDate;
      }

      const diffFull = nextRaceDate - pastRaceDate;
      const diffPast = now - pastRaceDate;
      const diff = (diffPast / diffFull) * 1725;

      setTimeout(() => {
        const elem = this.$refs.trackCircle;
        const circle = elem.querySelector('path');
        circle.setAttribute('stroke-dasharray', `${diff}, 1725`);
      }, 250);
    },
    handleCallback (callback) {
      if (typeof this.$refs.trackCircle !== 'undefined' && !this.circleDone) {
        const windowOffset = window.scrollY || window.pageYOffset;
        if (windowOffset <= callback) {
          this.fillCircle();
        }
      }
    },
    showIndex (driverResult) {
      return driverResult.time !== '' && (driverResult.behind === '' || !keywordsInBehind(driverResult.behind));
    },
    getBehind,
  },
  computed: {
    previousRace () {
      return this.getPreviousRace;
    },
    previousRaceResults () {
      return this.getPreviousRaceResults;
    },
    hasPreviousRaceResults () {
      return this.getPreviousRaceResults.length !== 0;
    },
    prevRaceDate () {
      return this.previousRace ? new Date(this.previousRace.raceStart) : null;
    },
    nextRace () {
      return this.getNextRace;
    },
    nextDate () {
      if (this.nextRace) {
        return new Date(this.nextRace.raceStart);
      }
      return null;
    },
    nextRaceDate () {
      return localizedDate(this.nextDate, this.countryString);
    },
    nextRaceTime () {
      return localizedTimeFromString(this.nextRace.raceStartLocal, this.countryString);
    },
    nextRaceViewerTime () {
      if (this.nextDate !== null) {
        return raceToViewerTime(this.nextRace, this.countryString);
      }

      return 'unknown';
    },
    nextRaceTrackId () {
      return this.nextRace
        ? this.nextRace.seasonEventTracks[this.nextRace.seasonEventTracks.length - 1].foreignId.toString()
        : '';
    },
    ...mapGetters([
      GetterTypes.isLoading,
      GetterTypes.championship,
      GetterTypes.seasonYear,
      GetterTypes.getNextRace,
      GetterTypes.getPreviousRace,
      GetterTypes.getPreviousRaceResults,
      RootGetterTypes.countryString,
    ]),
  },
  mounted () {
    if (this.previousRace && !this.nextRace && this.view === 'next') {
      this.changeActiveTab('previous');
    }

    const observer = new IntersectionObserver((observed) => {
      observed.forEach((element) => {
        if (element.target === this.$refs.seasonBox) {
          if (element.isIntersecting) {
            this.fillCircle();
          } else if (element.boundingClientRect.top > 0) {
            this.circleDone = false;
          } else {
            // the observer function is called ob init, so if the visitor has already scrolled
            // past this module and then reloads, we have to set the mask as if it was already faded in
            this.fillCircle();
          }
        }
      });
    }, {
      threshold: 0.75,
    });

    observer.observe(this.$refs.seasonBox);
  },
};
</script>

<style lang="scss">
  @import "../styles/components/seasonBoxView";
</style>
